import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBolt,
  faBuilding,
  faLayerGroup,
  faMoneyBillWave,
  faSliders,
  faTrophy,
} from '@fortawesome/free-solid-svg-icons';
import './dash.css';
import { logAdminActivity } from '../utils/activityLogger';
import {
  COUNTRY_OPTIONS,
  DATE_POSTED_OPTIONS,
  DEFAULT_FORM,
  JOB_TYPE_OPTIONS,
  fetchJobsScoutWithAuth,
  formatDateTime,
  formatCountryLabel,
  formatLabel,
  formatShortDate,
  parseKeywordList,
} from './jobsScoutShared';

function JobsScoutComp() {
  const auth = getAuth();
  const navigate = useNavigate();
  const [authReady, setAuthReady] = useState(Boolean(auth.currentUser));
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [runs, setRuns] = useState([]);
  const [search, setSearch] = useState('');
  const [runForm, setRunForm] = useState(DEFAULT_FORM);

  const summary = useMemo(() => {
    const companies = new Set();
    let withSalary = 0;

    jobs.forEach((job) => {
      if (job.companyName) companies.add(job.companyName);
      if (job.salary) withSalary += 1;
    });

    return {
      total: jobs.length,
      companies: companies.size,
      withSalary,
      latestRunCount: runs[0]?.savedCount || 0,
    };
  }, [jobs, runs]);

  const latestRun = runs[0] || null;
  const configuredKeywords = useMemo(
    () => parseKeywordList(runForm.includeKeyword),
    [runForm.includeKeyword],
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthReady(Boolean(user));
    });

    return () => unsubscribe();
  }, [auth]);

  const loadSavedConfig = useCallback(async () => {
    const configData = await fetchJobsScoutWithAuth(auth, '/jobs-config');
    if (configData?.config) {
      setRunForm((current) => ({
        ...current,
        ...configData.config,
      }));
    }
    return configData;
  }, [auth]);

  const loadData = useCallback(async ({ silent = false } = {}) => {
    if (!auth.currentUser) {
      setLoading(false);
      return;
    }

    if (!silent) {
      setLoading(true);
    }

    try {
      const searchParams = new URLSearchParams();
      searchParams.set('limit', '60');
      if (search.trim()) searchParams.set('search', search.trim());

      const [jobsData, runsData] = await Promise.all([
        fetchJobsScoutWithAuth(auth, `/jobs?${searchParams.toString()}`),
        fetchJobsScoutWithAuth(auth, '/jobs-runs'),
      ]);

      setJobs(Array.isArray(jobsData.jobs) ? jobsData.jobs : []);
      setRuns(Array.isArray(runsData.runs) ? runsData.runs : []);
    } catch (error) {
      console.error('Failed to load jobs scout data:', error);
      if (!silent) {
        swal('Could not load jobs', error.message || 'Please try again.', 'error');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [auth, search]);

  useEffect(() => {
    if (!authReady) return;
    loadData();
  }, [authReady, loadData]);

  useEffect(() => {
    if (!authReady || !auth.currentUser) return;
    loadSavedConfig().catch((error) => {
      console.error('Failed to load saved jobs config:', error);
    });
  }, [auth, authReady, loadSavedConfig]);

  const handleSaveConfig = async () => {
    if (!runForm.includeKeyword.trim()) {
      swal('Search required', 'Add the job keyword before saving the config.', 'warning');
      return;
    }

    setSavingConfig(true);
    try {
      const result = await fetchJobsScoutWithAuth(auth, '/jobs-config', {
        method: 'PUT',
        body: buildScoutPayload(),
      });

      setRunForm((current) => ({
        ...current,
        ...(result.config || {}),
      }));

      await logAdminActivity({
        action: 'Saved Jobs Scout Config',
        details: runForm.includeKeyword,
        meta: {
          source: 'jobs-scout',
          configId: result.configId || 'default',
        },
      });

      swal('Config saved', 'This jobs search setup is now stored in Firestore and can be reused later.', 'success');
    } catch (error) {
      console.error('Failed to save jobs config:', error);
      swal('Could not save config', error.message || 'Please try again.', 'error');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleRun = async () => {
    if (!runForm.includeKeyword.trim()) {
      swal('Search required', 'Add the job keyword you want to scrape first.', 'warning');
      return;
    }

    setRunning(true);
    try {
      const result = await fetchJobsScoutWithAuth(auth, '/jobs/run', {
        method: 'POST',
        body: buildScoutPayload(),
      });

      await logAdminActivity({
        action: 'Ran Jobs Scout',
        details: runForm.includeKeyword,
        meta: {
          source: 'jobs-scout',
          runId: result.runId || '',
          savedCount: result.savedCount || 0,
        },
      });

      await loadData({ silent: true });

      swal(
        'Jobs fetched',
        result.summary || `Saved ${result.savedCount || 0} jobs from the latest run.`,
        'success',
      );
    } catch (error) {
      console.error('Failed to run jobs scout:', error);
      if (error.runId || error.stage || Array.isArray(error.debugLogs)) {
        console.error('Jobs scout diagnostics:', {
          runId: error.runId,
          stage: error.stage,
          debugLogs: error.debugLogs,
          responseData: error.responseData,
        });
      }

      const details = [
        error.message || 'Please try again.',
        error.runId ? `Run ID: ${error.runId}` : '',
        error.stage ? `Stage: ${error.stage}` : '',
      ].filter(Boolean).join('\n');

      swal('Could not run jobs scout', details, 'error');
    } finally {
      setRunning(false);
    }
  };

  const handleOpenJob = (job) => {
    navigate(`/jobs-scout/${job.id}`, {
      state: {
        job,
      },
    });
  };

  const buildScoutPayload = () => {
    const payload = {
      ...runForm,
      pagesToFetch: Number(runForm.pagesToFetch) || 1,
    };

    if (!JOB_TYPE_OPTIONS.some((option) => option.value === payload.jobType && option.value)) {
      delete payload.jobType;
    }

    return payload;
  };

  const renderConfigHelp = (title, text) => (
    <div className='opportunityScoutConfigHelp'>
      <strong>{title}:</strong> {text}
    </div>
  );

  return (
    <div className='dashComp opportunityScoutPage'>
      <section className='opportunityScoutTopBar'>
        <div className='opportunityScoutTopBarTitle'>
          <h2>Jobs Scout</h2>
          <p>Scrape fresh job listings, review them in admin, and reuse the same saved jobs for the student dashboard later.</p>
          {latestRun && (
            <div className='opportunityScoutRunStamp'>
              Last run: {formatDateTime(latestRun.completedAtMs || latestRun.updatedAtMs)}
            </div>
          )}
        </div>
        <div className='opportunityScoutTopBarActions'>
          <Button variant='dark' className='opportunityScoutPrimaryButton' onClick={handleRun} disabled={running}>
            {running ? <Spinner animation='border' size='sm' /> : <FontAwesomeIcon icon={faBolt} />}
            <span>{running ? 'Running…' : 'Run'}</span>
          </Button>
          <Button
            variant='outline-dark'
            className='opportunityScoutSecondaryButton'
            onClick={() => setConfigModalOpen(true)}
          >
            <FontAwesomeIcon icon={faSliders} />
            <span>Search Config</span>
          </Button>
        </div>
      </section>

      <section className='opportunityScoutSummaryGrid'>
        <article className='opportunityScoutSummaryCard'>
          <div className='opportunityScoutSummaryIcon opportunityScoutSummaryIcon--blue'>
            <FontAwesomeIcon icon={faLayerGroup} />
          </div>
          <div>
            <p>Total jobs saved</p>
            <h3>{summary.total}</h3>
          </div>
        </article>
        <article className='opportunityScoutSummaryCard'>
          <div className='opportunityScoutSummaryIcon opportunityScoutSummaryIcon--gold'>
            <FontAwesomeIcon icon={faBuilding} />
          </div>
          <div>
            <p>Companies tracked</p>
            <h3>{summary.companies}</h3>
          </div>
        </article>
        <article className='opportunityScoutSummaryCard'>
          <div className='opportunityScoutSummaryIcon opportunityScoutSummaryIcon--green'>
            <FontAwesomeIcon icon={faMoneyBillWave} />
          </div>
          <div>
            <p>Jobs with salary</p>
            <h3>{summary.withSalary}</h3>
          </div>
        </article>
        <article className='opportunityScoutSummaryCard'>
          <div className='opportunityScoutSummaryIcon opportunityScoutSummaryIcon--rose'>
            <FontAwesomeIcon icon={faTrophy} />
          </div>
          <div>
            <p>Last run new jobs</p>
            <h3>{summary.latestRunCount}</h3>
          </div>
        </article>
      </section>

      <section className='opportunityScoutPanel'>
        <div className='opportunityScoutPanelHeader'>
          <div>
            <h4>Saved job listings</h4>
            <p>All saved jobs stay in Firestore. Click any row to open the full listing view with more details.</p>
          </div>
          {latestRun && (
            <div className='opportunityScoutRunMeta'>
              <span className={`opportunityScoutStatusBadge opportunityScoutStatusBadge--${latestRun.status || 'new'}`}>
                {formatLabel(latestRun.status || 'completed')}
              </span>
              <small>
                Last run {formatDateTime(latestRun.completedAtMs || latestRun.updatedAtMs)}
              </small>
            </div>
          )}
        </div>

        <div className='opportunityScoutFilters mb-3'>
          <Form.Control
            type='text'
            placeholder='Search title, company, location, source'
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        {latestRun && (
          <div className='opportunityScoutFooterMeta' style={{ marginBottom: '16px' }}>
            <span>Search: {latestRun.searchInput?.includeKeyword || runForm.includeKeyword || 'Not set'}</span>
            <span>Keyword searches: {latestRun.keywordCount || parseKeywordList(latestRun.searchInput?.includeKeyword).length || 1}</span>
            <span>New jobs saved: {latestRun.savedCount || 0}</span>
            <span>Skipped existing: {latestRun.skippedCount || 0}</span>
          </div>
        )}

        {loading ? (
          <div className='opportunityScoutLoadingState'>
            <Spinner animation='border' />
          </div>
        ) : jobs.length === 0 ? (
          <div className='opportunityScoutEmptyState'>
            <p>Run the jobs scraper to bring in your first listings.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className='table opportunityScoutTable'>
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Company</th>
                  <th>Location</th>
                  <th>Posted</th>
                  <th>Type</th>
                  <th>Salary</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr
                    key={job.id}
                    onClick={() => handleOpenJob(job)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div style={{ minWidth: '220px' }}>
                        <strong>{job.jobTitle || 'Untitled role'}</strong>
                        <div className='opportunityScoutMutedCopy' style={{ marginTop: '4px' }}>
                          {job.searchKeyword || 'No keyword captured'}
                        </div>
                      </div>
                    </td>
                    <td>{job.companyName || 'Unknown company'}</td>
                    <td>{job.location || 'Not captured'}</td>
                    <td>{job.postedDateText || formatShortDate(job.postedDateIso)}</td>
                    <td>{job.jobType || 'Any type'}</td>
                    <td>{job.salary || '—'}</td>
                    <td>{job.sourceDomain || job.postedVia || 'Google Jobs'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Modal
        show={configModalOpen}
        onHide={() => setConfigModalOpen(false)}
        centered
        size='xl'
        dialogClassName='opportunityScoutConfigModal'
        contentClassName='opportunityScoutConfigModalContent'
      >
        <Modal.Header closeButton className='opportunityScoutConfigModalHeader'>
          <Modal.Title>Jobs Search Config</Modal.Title>
        </Modal.Header>
        <Modal.Body className='opportunityScoutConfigModalBody'>
          <div className='opportunityScoutConfigHero'>
            <div>
              <p className='opportunityScoutEyebrow'>Reusable Search Setup</p>
              <h3>Define the jobs you want the scraper to keep bringing back.</h3>
              <p>
                Save this once and the same search setup will be reused for manual tests now and for the Monday scheduled scrape later.
              </p>
            </div>
            <div className='opportunityScoutConfigHeroBadges'>
              <span>Apify actor: Google Jobs</span>
              <span>Stored in Firestore</span>
              <span>Reused weekly</span>
            </div>
          </div>

          <div className='opportunityScoutConfigGrid'>
            <div className='opportunityScoutConfigMain'>
              <section className='opportunityScoutConfigSection'>
                <div className='opportunityScoutConfigSectionHead'>
                  <h4>Core search</h4>
                  <p>These fields decide what kind of jobs we search for first.</p>
                </div>
                <div className='opportunityScoutConfigFieldGrid opportunityScoutConfigFieldGrid--primary'>
                  <Form.Group className='opportunityScoutConfigFieldCard opportunityScoutConfigFieldCard--wide'>
                    <Form.Label>Keywords</Form.Label>
                    <Form.Control
                      type='text'
                      placeholder='Example: software developer, data scientist, ui/ux designer'
                      value={runForm.includeKeyword}
                      onChange={(event) => setRunForm((current) => ({ ...current, includeKeyword: event.target.value }))}
                    />
                    {renderConfigHelp('How to use it', 'Enter one or many job searches separated by commas. One run will search all of them in sequence.')}
                  </Form.Group>
                  <Form.Group className='opportunityScoutConfigFieldCard'>
                    <Form.Label>Location</Form.Label>
                    <Form.Control
                      type='text'
                      placeholder='Example: Nairobi'
                      value={runForm.locationName}
                      onChange={(event) => setRunForm((current) => ({ ...current, locationName: event.target.value }))}
                      disabled={runForm.countryName === 'remote'}
                    />
                    {renderConfigHelp(
                      'What this does',
                      runForm.countryName === 'remote'
                        ? 'Remote searches ignore the city filter so the scraper can stay broad.'
                        : 'Filters jobs to a city, region, or area when the source supports it.',
                    )}
                  </Form.Group>
                  <Form.Group className='opportunityScoutConfigFieldCard'>
                    <Form.Label>Company</Form.Label>
                    <Form.Control
                      type='text'
                      placeholder='Optional company name filter'
                      value={runForm.companyName}
                      onChange={(event) => setRunForm((current) => ({ ...current, companyName: event.target.value }))}
                    />
                    {renderConfigHelp('Optional', 'Use this only if you want jobs from one company or a narrow employer filter.')}
                  </Form.Group>
                </div>
              </section>

              <section className='opportunityScoutConfigSection'>
                <div className='opportunityScoutConfigSectionHead'>
                  <h4>Refine results</h4>
                  <p>Use these filters to narrow the results and manage cost.</p>
                </div>
                <div className='opportunityScoutConfigFieldGrid'>
                  <Form.Group className='opportunityScoutConfigFieldCard'>
                    <Form.Label>Country</Form.Label>
                    <Form.Select
                      value={runForm.countryName}
                      onChange={(event) => setRunForm((current) => ({
                        ...current,
                        countryName: event.target.value,
                        locationName: event.target.value === 'remote' ? '' : current.locationName,
                      }))}
                    >
                      {COUNTRY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </Form.Select>
                    {renderConfigHelp('What this does', 'Sets the country context for the Google Jobs scrape.')}
                  </Form.Group>
                  <Form.Group className='opportunityScoutConfigFieldCard'>
                    <Form.Label>Job type</Form.Label>
                    <Form.Select
                      value={runForm.jobType}
                      onChange={(event) => setRunForm((current) => ({ ...current, jobType: event.target.value }))}
                    >
                      {JOB_TYPE_OPTIONS.map((option) => (
                        <option key={option.value || 'all'} value={option.value}>{option.label}</option>
                      ))}
                    </Form.Select>
                    {renderConfigHelp('Optional', 'Limit results to full-time, part-time, contractor, or intern roles.')}
                  </Form.Group>
                  <Form.Group className='opportunityScoutConfigFieldCard'>
                    <Form.Label>Date posted</Form.Label>
                    <Form.Select
                      value={runForm.datePosted}
                      onChange={(event) => setRunForm((current) => ({ ...current, datePosted: event.target.value }))}
                    >
                      {DATE_POSTED_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </Form.Select>
                    {renderConfigHelp('Freshness', 'Restrict the scrape to jobs posted recently.')}
                  </Form.Group>
                  <Form.Group className='opportunityScoutConfigFieldCard'>
                    <Form.Label>Pages to fetch</Form.Label>
                    <Form.Control
                      type='number'
                      min='1'
                      max='10'
                      value={runForm.pagesToFetch}
                      onChange={(event) => setRunForm((current) => ({ ...current, pagesToFetch: event.target.value }))}
                    />
                    {renderConfigHelp('Cost and volume', 'Higher pages fetch more listings but also cost more and take longer.')}
                  </Form.Group>
                </div>
              </section>
            </div>

            <aside className='opportunityScoutConfigSidebar'>
              <div className='opportunityScoutConfigSideCard'>
                <p className='opportunityScoutEyebrow'>Current Summary</p>
                <h4>{configuredKeywords[0] || 'No keyword set yet'}</h4>
                <div className='opportunityScoutConfigSummaryList'>
                  <div>
                    <span>Keyword searches</span>
                    <strong>{configuredKeywords.length || 0}</strong>
                  </div>
                  <div>
                    <span>Location</span>
                    <strong>{runForm.locationName || 'Any location'}</strong>
                  </div>
                  <div>
                    <span>Country</span>
                    <strong>{formatCountryLabel(runForm.countryName) || 'Any country'}</strong>
                  </div>
                  <div>
                    <span>Job type</span>
                    <strong>{JOB_TYPE_OPTIONS.find((option) => option.value === runForm.jobType)?.label || 'Any job type'}</strong>
                  </div>
                  <div>
                    <span>Date posted</span>
                    <strong>{DATE_POSTED_OPTIONS.find((option) => option.value === runForm.datePosted)?.label || 'Last week'}</strong>
                  </div>
                  <div>
                    <span>Pages</span>
                    <strong>{runForm.pagesToFetch || 1}</strong>
                  </div>
                  <div>
                    <span>Company filter</span>
                    <strong>{runForm.companyName || 'None'}</strong>
                  </div>
                </div>
              </div>

              <div className='opportunityScoutConfigSideCard opportunityScoutConfigSideCard--soft'>
                <p className='opportunityScoutEyebrow'>Before You Save</p>
                <ul className='opportunityScoutConfigChecklist'>
                  <li>Separate multiple job searches with commas in the keywords field.</li>
                  <li>Use precise keywords so the jobs stay relevant.</li>
                  <li>Keep pages low while testing to control cost.</li>
                  <li>Save once, then reuse the same setup for manual and scheduled runs.</li>
                </ul>
              </div>
            </aside>
          </div>
        </Modal.Body>
        <Modal.Footer className='opportunityScoutConfigModalFooter'>
          <Button
            variant='outline-secondary'
            onClick={() => setRunForm(DEFAULT_FORM)}
            disabled={savingConfig}
          >
            Reset Defaults
          </Button>
          <Button
            variant='dark'
            onClick={handleSaveConfig}
            disabled={savingConfig}
          >
            {savingConfig ? <Spinner animation='border' size='sm' /> : null}
            <span style={{ marginLeft: savingConfig ? '8px' : 0 }}>
              {savingConfig ? 'Saving…' : 'Save Search Config'}
            </span>
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default JobsScoutComp;
